import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, ReceiptIndianRupee, Settings2, LogOut, Menu, X, ClipboardList, Boxes,
} from "lucide-react";
import { useAuth } from "./AuthContext";
import { LogoMark } from "../components/site/Shared";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["super_admin", "hr_admin", "finance", "project_manager", "employee"] },
  { to: "/portal/field", label: "Field Tracker", icon: ClipboardList, roles: ["super_admin", "hr_admin", "finance", "project_manager", "employee"] },
  { to: "/portal/inventory", label: "Inventory", icon: Boxes, roles: ["super_admin", "hr_admin", "finance", "project_manager"] },
  { to: "/portal/employees", label: "Employees", icon: Users, roles: ["super_admin", "hr_admin", "finance", "project_manager"] },
  { to: "/portal/payroll", label: "Payroll & Slips", icon: ReceiptIndianRupee, roles: ["super_admin", "hr_admin", "finance"] },
  { to: "/portal/settings", label: "Settings", icon: Settings2, roles: ["super_admin", "hr_admin"] },
];

const ROLE_LABELS = {
  super_admin: "Super Admin",
  hr_admin: "HR Admin",
  finance: "Finance",
  project_manager: "Project Manager",
  employee: "Employee",
};

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/portal/login");
  };

  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="px-6 h-[68px] flex items-center gap-3 border-b border-white/10">
        <LogoMark className="h-8 w-8" />
        <div className="leading-none">
          <div className="font-display font-extrabold text-white text-sm tracking-tight">A3 HR PORTAL</div>
          <div className="mono-label text-[9px] mt-1" style={{ color: "#64748B" }}>Management System</div>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {items.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().split(" ")[0]}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-200 border-l-2 ${
                  isActive
                    ? "bg-[#131C31] border-cyan text-white"
                    : "border-transparent text-slate-400 hover:text-white hover:bg-[#0C1322]"
                }`
              }
              style={({ isActive }) => (isActive ? { borderColor: "#00F0FF" } : {})}
            >
              <Icon size={18} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="px-2 mb-3">
          <div className="text-sm text-white font-medium truncate">{user?.name}</div>
          <div className="mono-label text-[10px] mt-1 text-cyan" style={{ color: "#00F0FF" }}>
            {ROLE_LABELS[user?.role] || user?.role}
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-button"
          className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-300 border border-white/10 hover:border-red-400/50 hover:text-red-300 transition-colors duration-200"
        >
          <LogOut size={16} /> Sign Out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060A14] text-white flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 bg-[#0C1322] border-r border-white/10 fixed inset-y-0 left-0 z-40">
        <SidebarInner />
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-[#0C1322] border-r border-white/10 h-full">
            <SidebarInner />
          </div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 glass-nav h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <LogoMark className="h-7 w-7" />
            <span className="font-display font-bold text-sm">A3 HR</span>
          </div>
          <button onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle" className="text-white">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        <main className="p-5 md:p-8 max-w-[1200px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
